import React from "react"
import { useTheme } from "@mui/material/styles"
import _last from "lodash/last"
import _takeRight from "lodash/takeRight"
import chroma from "chroma-js"
import styled from "styled-components"

import {
  AreaSeries,
  FlexibleWidthXYPlot,
  LineSeries,
  MarkSeries
} from "react-vis"

import { Typography } from "@mui/material"

type TExtraChartProps = {
  yDomain: number[] | null
}

interface IProps {
  /**
   * The index for the series colors from the theme. 1-5
   * @default 0
   */
  colorIndex?: number
  /**
   * The smoothing algorithm to apply to the line. A named curve function from the D3 shape library: https://github.com/d3/d3-shape#curves
   * @default "curveMonotoneX"
   */
  curve?: string
  /**
   * An array of y values
   */
  data: number[]
  /**
   * The height of the component. min = 30
   * @default 100
   */
  height?: number
  /**
   * Inverted background and series styles
   * @default false
   */
  invertedStyles?: boolean
  /**
   * Custom formatted for the latest value. Don't include prefix or suffix, us the other props.
   * @default v => v.toFixed(2)
   */
  lastValueFormatter?: (val: number) => string
  /**
   * How many data points to display at once (taken from end of data)
   * @default -1
   */
  limit?: number
  /**
   * The message to display in the absence of data
   * @default "No Data"
   */
  noDataMessage?: string
  /**
   * Offset end of sparkline from the right edge, to give the indication that it's not finished
   * @default false
   */
  offset?: boolean
  /**
   * Whether to show a dot at the end of the data, recommended for live and frequently changing data
   * @default false
   */
  showEndDot?: boolean
  /**
   * Whether to show the last value as a large text label
   * @default false
   */
  showLastValue?: boolean
  /**
   * Whether to show a horizontal dashed line
   * @default false
   */
  showThreshold?: boolean
  /**
   * The y-value specifying where the threshold line should be drawn
   * @default null
   */
  thresholdValue?: number | null
  /**
   * A label for the threshold line
   * @default ""
   */
  thresholdLabel?: string
  /**
   * The label which appears above the last value
   * @default ""
   */
  valueLabel?: string
  /**
   * The small prefix which appears before the value. e.g. $
   * @default ""
   */
  valueLabelPrefix?: string
  /**
   * The small prefix which appears after the value. e.g. %
   * @default ""
   */
  valueLabelSuffix?: string
  /**
   * Y-domain specifying min and max values for y-axis. Useful for aligning multiple charts displaying in-line. Will be auto-calculated from data is not specified.
   * @default [auto, auto]
   */
  yDomain?: number[]
}

const labelHeaderHeight = 68

const SparklineWrap = styled.div<{
  extended: boolean
  height: number
  invertedStyles: boolean
  color: string
}>`
  width: 100%;
  height: ${p => (p.extended ? p.height + labelHeaderHeight : p.height)}px;
  position: relative;
  padding: 10px 0 0 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${p =>
    p.invertedStyles
      ? "none"
      : `linear-gradient(0deg, ${chroma(p.color).alpha(0.2).css()} 0%, ${chroma(
          p.color
        )
          .alpha(0)
          .css()} 100%)`};
`

const NoDataWrap = styled.div`
  text-align: center;
  text-transform: uppercase;
`

const LabelWrap = styled.div`
  text-align: right;
  height: ${labelHeaderHeight}px;
  padding: 0.5rem 0.5rem 0 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  right: 0;
`

const ValueLabel = styled(Typography)`
  font-size: 0.6rem;
  text-transform: uppercase;
  margin: 0;
`

const LatestValue = styled(Typography)`
  font-size: 1.6rem;
  margin: 0;
`

const Prefix = styled.span`
  font-size: 1.2rem;
  margin: 0;
`
const Suffix = styled(Prefix)``

const defaultLastValueFormatter = (v: number) => v.toFixed(2)

export const Sparkline = ({
  data = [],
  colorIndex = 0,
  curve = "curveMonotoneX",
  height = 100,
  invertedStyles = false,
  lastValueFormatter = defaultLastValueFormatter,
  limit = -1,
  offset = false,
  noDataMessage = "No Data",
  showEndDot = false,
  showLastValue = false,
  showThreshold = false,
  thresholdValue = null,
  thresholdLabel = "",
  valueLabel = "",
  valueLabelPrefix = "",
  valueLabelSuffix = "",
  yDomain = []
}: IProps) => {
  const theme = useTheme()
  let lineData = data
  let lineColor = theme.series[0]
  if (limit > 0 && limit < data.length) {
    lineData = _takeRight(data, limit)
  }
  if (colorIndex > 0 && colorIndex < theme.series.length) {
    lineColor = theme.series[colorIndex]
  }
  const mappedLineData = lineData.map((y, i) => ({
    x: i,
    y
  }))

  const lastValElm =
    typeof mappedLineData !== "undefined" && mappedLineData.length
      ? _last(mappedLineData)
      : null

  const lastVal = lastValElm ? lastValElm.y : null

  const endDotData = [
    {
      x: mappedLineData.length - 1,
      y: lastVal
    }
  ]

  const hasData = mappedLineData && !!mappedLineData.length

  const isExtended = hasData && showLastValue

  const plotMargin = {
    top: isExtended ? labelHeaderHeight : 0,
    right: 0,
    bottom: 0,
    left: 0
  }

  const thresholdData = [
    { x: 0, y: thresholdValue },
    { x: mappedLineData.length - 1, y: thresholdValue }
  ]

  const minHeight = 30
  const componentHeight = Math.max(height, minHeight)

  const extraProps: TExtraChartProps = {
    yDomain: null
  }

  if (yDomain && yDomain.length === 2) {
    extraProps.yDomain = yDomain
  }

  return (
    <SparklineWrap
      height={componentHeight}
      extended={isExtended}
      color={lineColor}
      invertedStyles={invertedStyles}
      data-testid="wrapper"
    >
      {showLastValue && hasData ? (
        <LabelWrap>
          {valueLabel ? <ValueLabel>{valueLabel}</ValueLabel> : ""}
          <LatestValue variant="h3">
            {valueLabelPrefix ? <Prefix>{valueLabelPrefix}</Prefix> : ""}
            {lastVal !== null ? lastValueFormatter(lastVal) : ""}
            {valueLabelSuffix ? <Suffix>{valueLabelSuffix}</Suffix> : ""}
          </LatestValue>
        </LabelWrap>
      ) : (
        ""
      )}
      {hasData ? (
        <FlexibleWidthXYPlot
          height={componentHeight + (isExtended ? labelHeaderHeight : 0) - 10}
          margin={plotMargin}
          {...extraProps}
        >
          <LineSeries curve={curve} data={mappedLineData} stroke={lineColor} />
          <AreaSeries
            curve={curve}
            data={mappedLineData}
            fill={invertedStyles ? lineColor : theme.basePalette.bg}
            opacity={invertedStyles ? 0.2 : 0.6}
            stroke={"none"}
          />
          {showThreshold && (
            <LineSeries
              data={thresholdData}
              stroke={"rgba(255, 255, 255, 0.5)"}
              strokeStyle="dashed"
              strokeDasharray="[5,5]"
              strokeWidth={1}
            />
          )}
          {showEndDot && (
            <MarkSeries
              data={endDotData}
              style={{ fill: lineColor, stroke: "none" }}
              size={3}
            />
          )}
        </FlexibleWidthXYPlot>
      ) : (
        <NoDataWrap>
          <Typography>{noDataMessage}</Typography>
        </NoDataWrap>
      )}
    </SparklineWrap>
  )
}

export default Sparkline
